import { Alert, Box, Button, ButtonGroup, Chip, CircularProgress, Collapse, Dialog, DialogActions, DialogContent, DialogTitle, Divider, FormControl, IconButton, InputLabel, ListItemText, Menu, MenuItem, Select, Snackbar, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Tooltip, Typography, useMediaQuery, useTheme } from "@mui/material";
import { IFacturaMetodoPago, IFacturaResponse } from "./FacturarCitaDialog";
import { useState } from "react";
import React from "react";
import ConfigUtils from "../../../utils/Config";
import FacturacionService from "../../../services/FacturacionService";
import { useNavigate } from "react-router-dom";
import LogService from "../../../services/LogService";
import ILog from "../../../types/Log";
import * as constantes from "../../../common/Constantes";
import IAlert from "../../../types/Alert";
import DownloadIcon from '@mui/icons-material/GetApp';
import EmailIcon from '@mui/icons-material/Email';
import MoreIcon from '@mui/icons-material/MoreVert';
import DocUtils from "../../../utils/Doc";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import moment from "moment";
import { IConcepto, IFacturaData } from "./DetalleFactura";
import PacientesUtils from "../../../utils/Pacientes";
import { formatCurrency, formatNumeroFactura } from "../../../utils/Factura";
import { getFacturaAcciones } from "../common/facturaAcciones";
import type { IFacturaCompleta } from "./DetalleFacturaComplementaria";
import NumeroOrientativoEmision, { cargarNumeroOrientativo, InfoNumeroOrientativo } from "../common/NumeroOrientativoEmision";
import AnularFacturaDialog from "../common/AnularFacturaDialog";
import { chipEstadoFactura, chipEstadoPago } from "../common/chipsEstadoFactura";
import EliminarFacturaAnulada, { AvisoEliminacion } from "../common/EliminarFacturaAnulada";
import EnlaceFactura from "../common/EnlaceFactura";

interface DetalleFacturaParcialProps {
  facturaParcial: IFacturaData;
  facturaComplementaria?: IFacturaData;
  tipoFacturacion: string;
  pacienteId: string;
  email: string;
  authEmail: boolean;
  detalleCita: { fecha: string; hora: string };
  citaId: string;
  onFacturaPagada: (idServicio: string, factura: IFacturaResponse | null) => void;
  onFacturaEmitida: (idServicio: string, factura: IFacturaResponse | null) => void;
  onRectificarFactura: (factura: IFacturaData) => void;
  onPagarRectificada: (factura: IFacturaResponse | null) => void;
  onEmitirRectificada: (factura: IFacturaResponse | null) => void;
  pacienteMenor?: string;
  pacienteFechaNacimiento: boolean;
  onAnularFactura?: (_id: string, motivoAnulacion: string, anulacion?: IFacturaCompleta) => void;
  onFacturaEliminada?: (_id: string, aviso: AvisoEliminacion) => void;
  // El servidor indica (409) que la factura ya no está en el estado que mostrábamos: quien la contiene debe recargarla.
  onEstadoCambiado?: (mensaje: string) => void;
}

const DetalleFacturaParcial = ({ facturaParcial, facturaComplementaria, tipoFacturacion, citaId, onFacturaPagada, onFacturaEmitida, onRectificarFactura, onPagarRectificada, onEmitirRectificada, pacienteId, email, authEmail, detalleCita, pacienteMenor, pacienteFechaNacimiento, onAnularFactura, onFacturaEliminada, onEstadoCambiado }: DetalleFacturaParcialProps ) => {
  const monedaFactura = facturaParcial.moneda || 'EUR';
  // Una parcial con complementaria asociada no se puede anular sola: dejaría la
  // complementaria huérfana y el total fiscal de la pareja dejaría de cuadrar.
  const acciones = getFacturaAcciones({ ...facturaParcial, tieneOpcionAnular: !facturaComplementaria });
  const history = useNavigate();
  const [alert, setAlert] = useState<IAlert>({ open: false, severity: "info", msg: "" });
  const [expandirDetalle, setExpandirDetalle] = useState(false);
  const [modalPagar, setModalPagar] = useState(false);
  const [metodoPago, setMetodoPago] = useState<IFacturaMetodoPago | undefined>(undefined);
  const [errorMetodoPago, setErrorMetodoPago] = useState<string | undefined>(undefined);
  const [errorFechaPago, setErrorFechaPago] = useState<string | undefined>(undefined);
  const [modalEmitir, setModalEmitir] = useState(false);
  const [numeroOrientativo, setNumeroOrientativo] = useState<InfoNumeroOrientativo | null>(null);
  const [erroresPaciente, setErroresPaciente] = useState<string[]>([]);
  const [loadingPagar, setLoadingPagar] = useState(false);
  const [loadingEmitir, setLoadingEmitir] = useState(false);
  const [loadingEnviar, setLoadingEnviar] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [modalAnular, setModalAnular] = useState(false);
  const [motivoAnulacion, setMotivoAnulacion] = useState("");
  const [confirmarAnulacion, setConfirmarAnulacion] = useState(false);
  const [loadingAnular, setLoadingAnular] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const metodosPago = ConfigUtils.getMetodosPago();
  const userRole: number = ConfigUtils.getUserRole();

  const handleCloseAlert = () => setAlert({ open: false, severity: "info", msg: "" });

  const handleAnularFactura = async () => {
    try {
      setLoadingAnular(true);
      const response = await FacturacionService.anularFactura(facturaParcial._id, { motivoAnulacion, tipoPago: "parcial", estadoEsperado: facturaParcial.estado });
      setAlert({ open: true, severity: 'success', msg: response.data.message });
      setModalAnular(false);
      setMotivoAnulacion("");
      onAnularFactura?.(facturaParcial._id, motivoAnulacion, response.data.factura);
    } catch (e: any) {
      if (e.response?.status === 409) {
        setModalAnular(false);
        setMotivoAnulacion("");
        onEstadoCambiado?.(e.response.data.message);
        return;
      }
      if (e.response.status === 401) {
        localStorage.clear();
        history(constantes.R_HOME);
      } else {
        const error: ILog = { method: 'DetalleFacturaParcial.Component.anularFactura', level: 'error', message: e.message, meta: e.response };
        LogService.logError(error);
        setAlert({ open: true, severity: 'error', msg: e.response.data.message });
      }
    } finally {
      setLoadingAnular(false);
    }
  };

  const openModalPagar = () => {
    if (facturaParcial?.estadoPago === "Pagada" && facturaParcial.metodoPago) {
      setMetodoPago({
        nombre: facturaParcial.metodoPago.nombre,
        codigo: facturaParcial.metodoPago.codigo,
        fechaPago: moment(facturaParcial.metodoPago.fechaPago),
      });
    }
    setModalPagar(true);
  };

  const openModalEmitir = async () => {
    const errores: string[] = [];

    if (!facturaParcial.receptor?.nombre) errores.push("Nombre");
    if (!facturaParcial.receptor?.nif) errores.push("DNI");
    if (!pacienteFechaNacimiento) errores.push("Fecha de nacimiento");

    if (errores.length > 0) {
      setErroresPaciente(errores);
    }
    setNumeroOrientativo(errores.length === 0 ? await cargarNumeroOrientativo(facturaParcial._id) : null);
    setModalEmitir(true);
  };

  const closeModalPagar = () => {
    setMetodoPago(undefined);
    setErrorMetodoPago(undefined);
    setErrorFechaPago(undefined);
    setModalPagar(false);
  };

  const closeModalEmitir = () => {
    setErroresPaciente([]);
    setModalEmitir(false);
  };

  const pagarFactura = async () => {
    let hasError = false;

    if (!metodoPago) {
      setErrorMetodoPago("Debes seleccionar un método de pago");
      hasError = true;
    }
    if (!metodoPago?.fechaPago) {
      setErrorFechaPago("Debes seleccionar una fecha de pago");
      hasError = true;
    } else if (!moment.isMoment(metodoPago.fechaPago) || !metodoPago.fechaPago.isValid()) {
      setErrorFechaPago("Debes seleccionar una fecha de pago válida");
      hasError = true;
    }

    if (hasError) return;
    if (!metodoPago) return;

    setErrorMetodoPago(undefined);
    setErrorFechaPago(undefined);

    try {
      setLoadingPagar(true);
      const response = await FacturacionService.pagarFactura(facturaParcial._id, { 
        tipoPago: tipoFacturacion,
        metodoPago,
        servicioId: citaId,
        pacienteId: pacienteId,
      });

      if (facturaParcial.facturaOriginalId) onPagarRectificada(response.data);
      else onFacturaPagada(citaId, response.data);
      closeModalPagar();

    } catch (e: any) {
      if (e.response?.status === 401) {
        localStorage.clear();
        history(constantes.R_HOME);
      } else {
        const error: ILog = {
          method: "DetalleFacturaParcial.Component.pagarFactura",
          level: "error",
          message: e.message,
          meta: e.response,
        };
        LogService.logError(error);
        setAlert({ open: true, severity: "error", msg: e.response?.data?.message || e.message });
      }
    } finally {
      setLoadingPagar(false);
    }
  };

  const emitirFactura = async () => {
    try {
      setLoadingEmitir(true);
      const response = await FacturacionService.emitirFactura(facturaParcial._id, { 
        userId: ConfigUtils.getUserId(),
        tipoPago: tipoFacturacion,
        servicioId: citaId,
        pacienteId: pacienteId,
      });

      if (facturaParcial.facturaOriginalId) onEmitirRectificada(response.data);
      else onFacturaEmitida(citaId, response.data);
      closeModalEmitir();

    } catch (e: any) {
      if (e.response?.status === 401) {
        localStorage.clear();
        history(constantes.R_HOME);
      } else {
        const error: ILog = {
          method: "DetalleFacturaParcial.Component.emitirFactura",
          level: "error",
          message: e.message,
          meta: e.response,
        };
        LogService.logError(error);
        setAlert({ open: true, severity: "error", msg: e.response?.data?.message || e.message });
      }
    } finally {
      setLoadingEmitir(false);
    }
  };

  const handleDownloadFactura = () => {
    DocUtils.formatFactura(facturaParcial, true, tipoFacturacion, detalleCita, facturaComplementaria?.numero, pacienteMenor);
  };

  const handleEnviarFactura = async () => {
    try {
      setLoadingEnviar(true);
      const facturaPDF = DocUtils.formatFactura(facturaParcial, false, tipoFacturacion, detalleCita, facturaComplementaria?.numero, pacienteMenor);
      const response = await FacturacionService.sendFactura({ facturaId: facturaParcial._id, email, doc: facturaPDF, nombre: facturaParcial.receptor.nombre, numeroFactura: facturaParcial.numero });
      setAlert({ open: true, severity: 'success', msg: response.data.message });
    } catch (e: any) {
      if (e.response.status === 401) {
            localStorage.clear();
            history(constantes.R_HOME);
        } else {
            const error: ILog = { method: 'DetalleFacturaParcial.Component.sendFactura', level: 'error', message: e.message, meta: e.response };
            LogService.logError(error);
            setAlert({ open: true, severity: 'error', msg: e.response.data.message });
        }
    } finally {
      setLoadingEnviar(false);
    }
  };

  const { isDisabledEmail, tooltipMsg } = PacientesUtils.canSendFactura(
    loadingEnviar,
    authEmail,
    email,
    facturaParcial.estado,
    facturaParcial.facturaRectificadaId
  );
  
  return (
      <>
        {/* INFO GENERAL DE LA FACTURA */}
        <Box>
          <Box
            display="flex"
            justifyContent="space-between"
            gap={2}
          >
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 600 }}
            >
              Información de la factura
            </Typography>       

            {/* Acciones */}
            <Box display="flex" gap={0.5}>
              <Tooltip title="Descargar factura">
                <IconButton
                  size="small"
                  onClick={handleDownloadFactura}
                >
                  <DownloadIcon fontSize="small" />
                </IconButton>
              </Tooltip>        

              <Tooltip title={facturaParcial.estado ==="Anulada" ? "Factura anulada" : tooltipMsg}>
                <span>
                  <IconButton
                    size="small"
                    onClick={handleEnviarFactura}
                    disabled={isDisabledEmail || facturaParcial.estado === "Anulada"}
                  >
                    {loadingEnviar ? (
                      <CircularProgress size={18} />
                    ) : (
                      <EmailIcon fontSize="small" />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          </Box>        
        </Box>

        <Box mb={1}>
          {/* --- Fila principal: Nº, Fecha, Estado --- */}
          <Stack
            direction={isMobile ? "column" : "row"}
            justifyContent="flex-start"
            alignItems={isMobile ? "flex-start" : "center"}
            spacing={isMobile ? 1 : 2}
          >
            {/* Nº */}
            <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 120 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Nº
              </Typography>
              <Typography variant="body2">{formatNumeroFactura(facturaParcial.numero)}</Typography>
            </Stack>  
            {/* Fecha */}
            <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 120 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Fecha
              </Typography>
              <Typography variant="body2">{new Date(facturaParcial.fechaFactura!).toLocaleDateString()}</Typography>
            </Stack>  
            {/* Estado */}
            <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 140 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Estado
              </Typography>
              {facturaParcial?.estado === "Anulada" ? (
                <Tooltip
                  title={facturaParcial?.motivoAnulacion}
                  placement="top"
                  arrow
                >
                  <Chip
                    label={facturaParcial?.estado}
                    size="small"
                    sx={{ textTransform: "capitalize", border: `1px solid #d32f2f`, color: "#d32f2f" }}
                  />
                </Tooltip>  
              ) : (
                <Chip
                {...chipEstadoFactura(facturaParcial)}
                size="small"
              />
              )}
            </Stack>
          </Stack>
          {/* --- Fila secundaria: Factura complementaria --- */}
          {facturaComplementaria && (
            <Typography variant="subtitle2" sx={{ mt: 0.5 }}>
              <Box component="span" sx={{ fontWeight: 600 }}>
                Factura complementaria Nº:{" "}
              </Box>
              <Box component="span" sx={{ fontWeight: "normal" }}>
                <EnlaceFactura facturaId={facturaComplementaria._id} numero={facturaComplementaria.numero} />
              </Box>
            </Typography>
          )}
        
          {/* --- Fila final: Información de rectificación --- */}
          {(facturaParcial?.facturaOriginalId || facturaParcial?.facturaRectificadaId) && (
            <Stack direction="column" spacing={0.5} sx={{ mt: 0.5 }}>
              {facturaParcial?.facturaOriginalId && (
                <Stack direction="row" spacing={1}>
                  <Typography variant="body2" sx={{ fontStyle: "italic", color: "text.secondary" }}>
                    {facturaParcial?.tipoRectificacion === "anulacion" ? "Anulación de la factura nº" : "Rectificativa de la factura nº"}
                  </Typography>
                  <EnlaceFactura facturaId={facturaParcial.facturaOriginalRef} numero={facturaParcial.facturaOriginalId} cursiva />
                </Stack>
              )}
              {facturaParcial?.facturaRectificadaId && (
                <Stack direction="row" spacing={1}>
                  <Typography variant="body2" sx={{ fontStyle: "italic", color: "text.secondary" }}>
                    {facturaParcial?.estado === "Anulada" ? "Anulada por la factura nº" : "Rectificada por la factura nº"}
                  </Typography>
                  <EnlaceFactura facturaId={facturaParcial.facturaRectificadaId} numero={facturaParcial.facturaRectificada} cursiva />
                </Stack>
              )}
            </Stack>
          )}
        </Box>
                  
        <Divider sx={{ mb: 2 }} />

        <Box
          display="flex"
          flexDirection={isMobile ? "column" : "row"}
          justifyContent="space-between"
          alignItems={isMobile ? "stretch" : "center"}
          mb={2}
          gap={isMobile ? 1.5 : 4}
        >
          {/* INFO IZQUIERDA */}
          <Box display="flex" flexDirection="column" gap={isMobile ? 0.75 : 1}>
            {/* Estado de pago */}
            <Box display="flex" justifyContent="space-between" alignItems="center" gap={{ xs: 0, sm: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Estado
              </Typography>
              <Chip
                {...chipEstadoPago(facturaParcial)}
                size="small"
              />
            </Box>

            {/* Tipo de facturación */}
            <Box display="flex" justifyContent="space-between" alignItems="center" gap={{ xs: 0, sm: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Tipo
              </Typography>
              <Chip
                label={tipoFacturacion === "total" ? "Total" : "Parcial"}
                size="small"
                color="info"
              />
            </Box>

            {/* Servicio */}
            <Box display="flex" justifyContent="space-between" gap={{ xs: 0, sm: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Servicio
              </Typography>
              <Typography variant="body2" noWrap>
                {facturaParcial.servicio}
              </Typography>
            </Box>
          </Box>

          {/* IMPORTE */}
          <Box
            display="flex"
            flexDirection="column"
            alignItems={isMobile ? "flex-start" : "flex-end"}
            mt={isMobile ? 1 : 0}
          >
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              Importe
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: "bold" }}>
              {formatCurrency(Number(facturaParcial.importeTotal), monedaFactura)}
            </Typography>
          </Box>
        </Box>

        {/* Si la factura está pagada, método de pago */}
        {facturaParcial.estadoPago === "Pagada" && (
          <Box
            mb={3}
            p={2}
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
              backgroundColor: "background.paper",
              maxWidth: 400,
            }}
          >
            <Box display="flex" flexDirection="column" gap={0.5}>
              <Typography variant="body2">
                <b>Método de pago:</b> {facturaParcial.metodoPago.nombre}
              </Typography>
              <Typography variant="body2">
                <b>Fecha de pago:</b> {moment(facturaParcial.metodoPago.fechaPago).format('DD/MM/YYYY')}
              </Typography>
            </Box>
          </Box>
        )}

        <Divider />

       <Box mt={2} display="flex" justifyContent="space-between">
        <Button 
          variant="outlined" 
          size="small" 
          onClick={() => setExpandirDetalle(!expandirDetalle)}
        >
          {expandirDetalle ? "Ocultar detalles" : "Ver detalles"}
        </Button>

        <Box display="flex" gap={1} alignItems="center">

        {acciones.mostrarBotonesAccion && (
          <ButtonGroup variant="outlined" color="success">
            {acciones.mostrarPagar && (
              <Button variant={acciones.variantPagar} onClick={openModalPagar}>Pagar</Button>
            )}
            {acciones.mostrarEmitir && (
              <Button variant={acciones.variantEmitir} onClick={openModalEmitir}>Emitir</Button>
            )}
          </ButtonGroup>
        )}
        <EliminarFacturaAnulada factura={facturaParcial} onEliminada={(aviso) => { onFacturaEliminada?.(facturaParcial._id, aviso); }} />
        {userRole === constantes.ROLES.ADMINISTRADOR &&
          acciones.mostrarMenuAcciones && (
            <>
              <Tooltip title="Más acciones">
                <IconButton
                  size="small"
                  onClick={(e) => setAnchorEl(e.currentTarget)}
                >
                  <MoreIcon fontSize="small" />
                </IconButton>
              </Tooltip>

              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={() => setAnchorEl(null)}
              >
                {acciones.mostrarCorregirCobro && <MenuItem
                  onClick={() => {
                    setAnchorEl(null);
                    openModalPagar();
                  }}
                >
                  <ListItemText>
                    Corregir cobro
                  </ListItemText>
                </MenuItem>}
                {acciones.mostrarRectificar && <MenuItem
                  onClick={() => {
                    setAnchorEl(null);
                    onRectificarFactura(facturaParcial);
                  }}
                >
                  <ListItemText>
                    Rectificar factura
                  </ListItemText>
                </MenuItem>}

                {onAnularFactura && !facturaComplementaria && <MenuItem
                  onClick={() => {
                    setAnchorEl(null);
                    setModalAnular(true);
                  }}
                >
                  <ListItemText>
                    Anular factura
                  </ListItemText>
                </MenuItem>}

              </Menu>
            </>
        )}
      </Box>
      </Box>

        {/* Bloque expandible */}
        <Collapse in={expandirDetalle} timeout="auto" unmountOnExit>
          <Box
            mt={2}
            p={2}
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
              backgroundColor: "background.paper",
              overflowX: "auto",
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
              Detalle de conceptos
            </Typography>
          
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Concepto</TableCell>
                  <TableCell align="right">Cantidad</TableCell>
                  <TableCell align="right">Precio unit.</TableCell>
                  <TableCell align="right">Subtotal</TableCell>
                </TableRow>
              </TableHead>
          
              <TableBody>
                {facturaParcial.conceptos.map((c: IConcepto, index: number) => {
                  const imp = facturaParcial.impuestos?.[index];
                  const base = c.cantidad * c.precio;
                  const cuota = imp ? base * (imp.cuota / 100) : 0;
                  const total = base + cuota;
                
                  return (
                    <React.Fragment key={index}>
                      {/* LÍNEA PRINCIPAL */}
                      <TableRow hover>
                        <TableCell>{c.nombre}</TableCell>
                        <TableCell align="right">{c.cantidad}</TableCell>
                        <TableCell align="right">
                          {formatCurrency(c.precio, monedaFactura)}
                        </TableCell>
                        <TableCell align="right">
                          {formatCurrency(base, monedaFactura)}
                        </TableCell>
                      </TableRow>
                  
                      {/* LÍNEA DETALLE: IMPUESTO + TOTAL */}
                      {imp && (
                        <TableRow>
                          <TableCell
                            colSpan={4}
                            sx={{
                              pl: 6,
                              py: 0.8,
                              backgroundColor: "action.hover",
                              borderBottom: "none",
                            }}
                          >
                            <Typography variant="body2" color="text.secondary">
                              {imp.tipo} ({imp.cuota}%): {formatCurrency(cuota, monedaFactura)}
                              &nbsp;•&nbsp;
                              <strong>Total: {formatCurrency(total, monedaFactura)}</strong>
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </Box>
        </Collapse>

        {/* Modal pagar */}
        {modalPagar && (
          <Dialog open={modalPagar} onClose={loadingPagar ? undefined : closeModalPagar} maxWidth="xs" fullWidth>
            <DialogTitle sx={{ fontSize: "1.3rem", fontWeight: "bold" }}>
              {facturaParcial.estadoPago === "Pagada" ? "Corregir cobro" : "Pagar factura"}
            </DialogTitle>
            <DialogContent sx={{ pt: 1 }}>
              {/* Importe total */}
              <Box
                sx={{
                  mb: 3,
                  mt: 1,
                  p: 2,
                  borderRadius: 2,
                  bgcolor: "grey.100",
                  textAlign: "center"
                }}
              >
                <Typography variant="h4" sx={{ fontWeight: "bold", color: "success.main" }}>
                  {formatCurrency(Number(facturaParcial.importeTotal), monedaFactura)}
                </Typography>
              </Box>
              
              {/* Método de pago */}
              <FormControl fullWidth error={!!errorMetodoPago}>
                <InputLabel id="metodo-pago-label">Método de pago*</InputLabel>
                <Select
                  labelId="metodo-pago-label"
                  label="Método de pago*"
                  value={metodoPago?.nombre || ""}
                  onChange={(e) => {
                    const nombre = e.target.value;
                    const codigo = metodosPago.find((m) => m.nombre === nombre)?.codigo!;
                    setMetodoPago({ nombre, codigo, fechaPago: moment() });
                    setErrorMetodoPago(undefined);
                  }}
                >
                  {metodosPago
                    .filter((m) => m.activo)
                    .map((m) => (
                      <MenuItem key={m.codigo} value={m.nombre}>
                        {m.nombre}
                      </MenuItem>
                    ))}
                </Select>
                  
                {errorMetodoPago && (
                  <Typography color="error" variant="caption" sx={{ mt: 0.5 }}>
                    {errorMetodoPago}
                  </Typography>
                )}
              </FormControl>
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <DatePicker
                  label="Fecha de pago"
                  value={metodoPago?.fechaPago}
                  onChange={(date) => {
                    if (metodoPago) {
                      setMetodoPago({ ...metodoPago, fechaPago: date ?? moment() });
                      setErrorFechaPago(undefined);
                    }
                  }}
                  renderInput={(params) => <TextField required fullWidth sx={{ mt: 2 }} error={!!errorFechaPago} helperText={errorFechaPago} {...params} />}
                />
              </LocalizationProvider>
            </DialogContent>              
            <DialogActions sx={{ p: 2 }}>
              <Button 
                variant="contained" 
                color="success"
                onClick={pagarFactura}
                disabled={loadingPagar}
                startIcon={loadingPagar && <CircularProgress size={18} />}
              >
                Pagar
              </Button>
              
              <Button 
                variant="outlined" 
                color="inherit"
                onClick={closeModalPagar}
                disabled={loadingPagar}
              >
                Cancelar
              </Button>
            </DialogActions>
          </Dialog>
        )}

        {/* Modal emitir */}
        {modalEmitir && 
        <Dialog open={modalEmitir} onClose={loadingEmitir ? undefined : closeModalEmitir} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ fontSize: "1.3rem", fontWeight: "bold" }}>Emitir factura</DialogTitle>
          <DialogContent sx={{ pt: 1 }}>
            {erroresPaciente.length > 0 ? (
              <Alert severity="error" sx={{ mt: 2 }}>
                No se puede emitir la factura. Faltan datos importantes:
                <ul>
                  {erroresPaciente.map((e) => (
                    <li key={e}>{e}</li>
                  ))}
                </ul>
              </Alert>
            ) : 
            <>
            <Alert severity="warning" sx={{ mb: 2 }}>
              {!facturaParcial.receptor?.direccion && (
                <>La factura se emitirá sin dirección del paciente.<br /><br /></>
              )}
              Una vez emitida, la factura no se podrá modificar.
              Solo se podrá generar una factura rectificativa.
            </Alert>

            <NumeroOrientativoEmision info={numeroOrientativo} />


            <Typography>
              ¿Deseas emitir esta factura definitivamente?
            </Typography>
            </>
            }

          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            {erroresPaciente.length === 0 ? (
              <>
                <Button 
                  variant="contained" 
                  color="success"
                  onClick={emitirFactura}
                  disabled={loadingEmitir}
                  startIcon={loadingEmitir && <CircularProgress size={18} />}
                >
                  Emitir
                </Button>
            
                <Button 
                  variant="outlined" 
                  color="inherit"
                  onClick={closeModalEmitir}
                  disabled={loadingEmitir}
                >
                  Cancelar
                </Button>
              </>
            ) : (
              <Button 
                variant="outlined" 
                color="inherit"
                onClick={closeModalEmitir}
              >
                Cerrar
              </Button>
            )}
          </DialogActions>
        </Dialog>}

        {/* Modal anular */}
        {modalAnular &&
        <AnularFacturaDialog
          open={modalAnular}
          loading={loadingAnular}
          numero={facturaParcial.numero}
          esBorrador={facturaParcial.estado === "Borrador"}
          esRectificativa={!!facturaParcial.facturaOriginalId}
          motivo={motivoAnulacion}
          onMotivoChange={setMotivoAnulacion}
          confirmar={confirmarAnulacion}
          onConfirmarChange={setConfirmarAnulacion}
          onClose={() => setModalAnular(false)}
          onConfirm={handleAnularFactura}
        />}

        <Snackbar open={alert.open} autoHideDuration={6000} onClose={handleCloseAlert}>
          <Alert variant="filled"  onClose={handleCloseAlert} severity={alert.severity} sx={{ width: "100%" }}>
            {alert.msg}
          </Alert>
        </Snackbar>
      </>

    );
};

export default DetalleFacturaParcial;